"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { FormattedTime } from "@defuse/ui/formatted-time";
import { Price } from "@defuse/ui/price";
import { useTokens } from "@defuse/ui/tokens-provider";
import { QueryFailed } from "@/components/query-failed";
import { getIntentLink, Short } from "@/components/short";
import { Badge } from "@defuse/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@defuse/ui/collapsible";
import { isErr } from "@intents-explorer/shared-api/result";
import { addDecimalPoint } from "@intents-explorer/shared-api/add-decimal-point";
import { NEAR_TX_EXPLORER_URL } from "@intents-explorer/shared-api/chains";
import type { TokenDiff } from "@/lib/clickhouse";
import type { TokenDiffs } from "@/lib/getTokenDiffs";
import { getTokenDiffs } from "@/lib/getTokenDiffs";
import { diffStatusToHumanReadable } from "@intents-explorer/shared-api/status";
import type { TransactionDetails } from "@/lib/transactions/api/getTransactionByDepositAddress";
import { pluralize } from "@/lib/pluralize";
import { unknownToStr } from "@/lib/type-safe-stringify";
import { cn } from "@/lib/utils";
export function SwapSteps(t0) {
  const $ = _c(28);
  const {
    transaction
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] !== transaction.intentHashes) {
    t1 = transaction.intentHashes !== null && transaction.intentHashes !== "" ? transaction.intentHashes.split(",") : [];
    $[0] = transaction.intentHashes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const intentHashes = t1;
  let t2;
  let t3;
  if ($[2] !== intentHashes || $[3] !== transaction.destinationAsset || $[4] !== transaction.originAsset) {
    t2 = ["tokenDiffs", intentHashes, transaction.originAsset, transaction.destinationAsset];
    t3 = async () => {
      const result = await getTokenDiffs(intentHashes, transaction.originAsset, transaction.destinationAsset);
      if (isErr(result)) {
        throw new Error("Failed to load swap steps", {
          cause: result.err
        });
      }
      return result.ok;
    };
    $[2] = intentHashes;
    $[3] = transaction.destinationAsset;
    $[4] = transaction.originAsset;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  const t4 = isOpen && intentHashes.length > 0;
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      retry: false,
      staleTime: Number.POSITIVE_INFINITY
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    data: tokenDiffs,
    isLoading,
    error
  } = useQuery(t5);
  let t6;
  if ($[11] !== tokenDiffs) {
    t6 = Array.from((tokenDiffs ?? []).reduce(_temp, new Set()));
    $[11] = tokenDiffs;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const txHashes = t6;
  if (intentHashes.length === 0) {
    return null;
  }
  const t7 = !isOpen && "rounded-bl-lg border-b dark:border-b";
  let t8;
  if ($[13] !== t7) {
    t8 = cn("text-foreground/90 dark:bg-background/50 dark:text-foreground border-border flex w-[calc(100%+16px)] items-center justify-between rounded-tl-lg border-t border-l bg-white/70 px-3 py-2 text-sm font-medium transition-colors hover:bg-white/80", t7);
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t10;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="mr-1">View All Swap Steps</span>;
    t10 = <ChevronDown className="h-3 w-3" />;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  let t11;
  if ($[17] !== t8) {
    t11 = <CollapsibleTrigger className={t8}>{t9}{t10}</CollapsibleTrigger>;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== error || $[20] !== isLoading || $[21] !== tokenDiffs || $[22] !== txHashes) {
    t12 = <CollapsibleContent>{isLoading ? <TokenDiffsSkeleton /> : error !== null ? <div className="dark:bg-background/60 border-border w-[calc(100%+16px)] rounded-bl-lg border-b border-l bg-white/85 px-4 text-sm"><QueryFailed what="swap steps" correlationId={typeof error.cause === "string" ? error.cause : ""} align="left" /></div> : tokenDiffs === undefined ? <div className="text-muted-foreground dark:bg-background/60 border-border w-[calc(100%+16px)] rounded-bl-lg border-b border-l bg-white/85 px-4 py-4 text-sm">No swap steps found for this transaction.</div> : <TokenDiffsContent tokenDiffs={tokenDiffs} txHashes={txHashes} />}</CollapsibleContent>;
    $[19] = error;
    $[20] = isLoading;
    $[21] = tokenDiffs;
    $[22] = txHashes;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== isOpen || $[25] !== t11 || $[26] !== t12) {
    t13 = <Collapsible open={isOpen} onOpenChange={setIsOpen}>{t11}{t12}</Collapsible>;
    $[24] = isOpen;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
}
function _temp(acc, t0) {
  const [diffIn, diffOut] = t0;
  if (diffIn !== undefined && diffIn.txHash !== null && diffIn.txHash !== "") {
    acc.add(diffIn.txHash);
  }
  if (diffOut !== undefined && diffOut.txHash !== null && diffOut.txHash !== "") {
    acc.add(diffOut.txHash);
  }
  return acc;
}
function TokenDiffsContent(t0) {
  const $ = _c(10);
  const {
    tokenDiffs,
    txHashes
  } = t0;
  if (tokenDiffs.length === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="text-muted-foreground dark:bg-background/60 border-border w-[calc(100%+16px)] rounded-bl-lg border-b border-l bg-white/85 px-4 py-4 text-sm">No swap steps found for this transaction.</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== txHashes) {
    t1 = txHashes.length > 0 && <div className="text-foreground dark:bg-background/60 border-border flex w-[calc(100%+16px)] flex-wrap items-center gap-x-2 gap-y-1 border-l bg-white/85 px-3 py-2 text-sm"><span className="text-muted-foreground">Transaction {pluralize(txHashes.length, "Hash")}:</span>{txHashes.map(_temp2)}</div>;
    $[1] = txHashes;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== tokenDiffs) {
    t2 = tokenDiffs.map(_temp3);
    $[3] = tokenDiffs;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <div className="text-foreground dark:bg-background/60 border-border w-[calc(100%+16px)] rounded-bl-lg border-b border-l bg-white/85 text-sm">{t2}</div>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <>{t1}{t3}</>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function _temp3(t0) {
  const [diffIn, diffOut] = t0;
  const key = `${unknownToStr(diffIn?.accountId ?? null)}-${unknownToStr(diffIn?.tokenIn ?? null)}-${unknownToStr(diffIn?.amountIn ?? null)}-${unknownToStr(diffOut?.accountId ?? null)}-${unknownToStr(diffOut?.tokenOut ?? null)}-${unknownToStr(diffOut?.amountOut ?? null)}-${unknownToStr(diffIn?.txHash ?? null)}-${unknownToStr(diffOut?.txHash ?? null)}-${unknownToStr(diffIn?.intentHash ?? null)}-${unknownToStr(diffOut?.intentHash ?? null)}`;
  return <TokenDiffPair key={key} diffIn={diffIn} diffOut={diffOut} />;
}
function _temp2(txHash) {
  return <Short key={txHash} link={`${NEAR_TX_EXPLORER_URL}${txHash}`} text={txHash} />;
}
function TokenDiffPair(t0) {
  const $ = _c(36);
  const {
    diffIn,
    diffOut,
    className
  } = t0;
  const executionStatus = diffIn?.executionStatus ?? diffOut?.executionStatus;
  const blockTimestamp = diffIn?.blockTimestamp ?? diffOut?.blockTimestamp;
  const blockHash = diffIn?.blockHash ?? diffOut?.blockHash;
  const intentHash = diffIn?.intentHash ?? diffOut?.intentHash;
  let t1;
  if ($[0] !== className) {
    t1 = cn("border-t px-4 py-4 first:pt-5 last:pb-5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== executionStatus) {
    t2 = executionStatus !== undefined && executionStatus !== "" && <Badge variant="outline" className="text-xs">{diffStatusToHumanReadable(executionStatus)}</Badge>;
    $[2] = executionStatus;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="flex items-center gap-2">{t2}</div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== blockTimestamp) {
    t4 = blockTimestamp !== undefined && blockTimestamp !== "" && <FormattedTime date={blockTimestamp} />;
    $[6] = blockTimestamp;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <span className="text-muted-foreground flex flex-wrap gap-2 text-sm">{t4}</span>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = <div className="mb-3 flex flex-wrap items-center justify-between gap-2">{t3}{t5}</div>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== diffIn) {
    t7 = <TokenDiffDisplay diff={diffIn} isTokenIn={true} />;
    $[13] = diffIn;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== diffOut) {
    t8 = <TokenDiffDisplay diff={diffOut} isTokenIn={false} />;
    $[15] = diffOut;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{t7}{t8}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== intentHash) {
    t10 = intentHash !== undefined && intentHash !== null && intentHash !== "" && <div>Intent Hash:{" "}<Short text={intentHash} link={getIntentLink(intentHash)} /></div>;
    $[20] = intentHash;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10) {
    t11 = <div className="mr-1 flex flex-col gap-1">{t10}</div>;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== blockHash) {
    t12 = blockHash !== undefined && blockHash !== "" && <div>Block Hash: <Short text={blockHash} /></div>;
    $[24] = blockHash;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12) {
    t13 = <div className="flex flex-col gap-1">{t12}</div>;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t11 || $[29] !== t13) {
    t14 = <div className="text-muted-foreground mt-3 flex flex-wrap justify-between text-xs sm:flex-row sm:items-end">{t11}{t13}</div>;
    $[28] = t11;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t1 || $[32] !== t14 || $[33] !== t6 || $[34] !== t9) {
    t15 = <div className={t1}>{t6}{t9}{t14}</div>;
    $[31] = t1;
    $[32] = t14;
    $[33] = t6;
    $[34] = t9;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
}
interface TokenDiffDisplayProps {
  diff: TokenDiff | undefined;
  isTokenIn: boolean;
}
function TokenDiffDisplay(t0) {
  const $ = _c(18);
  const {
    diff,
    isTokenIn
  } = t0;
  if (diff === undefined) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const {
    accountId,
    amountIn,
    tokenIn,
    amountOut,
    tokenOut,
    amountFee,
    tokenFee
  } = diff;
  const t1 = isTokenIn ? "From " : "To ";
  let t2;
  if ($[1] !== t1) {
    t2 = <span className="font-bold">{t1}</span>;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = accountId ?? "Unknown";
  let t4;
  if ($[3] !== t3) {
    t4 = <Short text={t3} />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <div className="mb-2 text-sm">{t2}{t4}</div>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = isTokenIn ? amountIn : amountOut;
  const t7 = isTokenIn ? tokenIn : tokenOut;
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = <MainTokenPriceDisplay amount={t6} tokenId={t7} />;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== amountFee || $[12] !== tokenFee) {
    t9 = <FeeDisplay amountFee={amountFee} tokenFee={tokenFee} />;
    $[11] = amountFee;
    $[12] = tokenFee;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t5 || $[15] !== t8 || $[16] !== t9) {
    t10 = <div className="flex flex-col space-y-1">{t5}{t8}{t9}</div>;
    $[14] = t5;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}
interface MainTokenPriceDisplayProps {
  amount: string | null;
  tokenId: string | null;
}
function MainTokenPriceDisplay(t0) {
  const $ = _c(6);
  const {
    amount,
    tokenId
  } = t0;
  const tokens = useTokens();
  if (tokenId === null || tokenId === "" || amount === null || amount === "") {
    return null;
  }
  const decimals = tokens[tokenId]?.decimals ?? 0;
  let t1;
  if ($[0] !== amount || $[1] !== decimals) {
    t1 = addDecimalPoint(amount, decimals);
    $[0] = amount;
    $[1] = decimals;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const priceFormatted = t1;
  let t2;
  if ($[3] !== priceFormatted || $[4] !== tokenId) {
    t2 = <Price price={priceFormatted} defuseAssetId={tokenId} />;
    $[3] = priceFormatted;
    $[4] = tokenId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
interface FeeDisplayProps {
  amountFee: string | null;
  tokenFee: string | null;
}
function FeeDisplay(t0) {
  const $ = _c(7);
  const {
    amountFee,
    tokenFee
  } = t0;
  const tokens = useTokens();
  if (tokenFee === null || tokenFee === "" || amountFee === null || amountFee === "") {
    return null;
  }
  const decimals = tokens[tokenFee]?.decimals ?? 0;
  let t1;
  if ($[0] !== amountFee || $[1] !== decimals) {
    t1 = addDecimalPoint(amountFee, decimals);
    $[0] = amountFee;
    $[1] = decimals;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const feeFormatted = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mb-1">Fee: </div>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== feeFormatted || $[5] !== tokenFee) {
    t3 = <div className="text-muted-foreground mt-2 mb-2 text-sm">{t2}<Price price={feeFormatted} defuseAssetId={tokenFee} /></div>;
    $[4] = feeFormatted;
    $[5] = tokenFee;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function TokenDiffsSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="text-foreground dark:bg-background/60 border-border flex flex-wrap items-center gap-x-2 gap-y-1 border-l bg-white/85 px-3 py-2 text-sm"><div className="bg-brand-grey/25 h-4 w-32 animate-pulse rounded" /><div className="bg-brand-grey/25 h-4 w-20 animate-pulse rounded" /><div className="bg-brand-grey/25 h-4 w-24 animate-pulse rounded" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="w-[calc(100%+16px)]">{t0}<div className="text-foreground dark:bg-background/60 border-border overflow-hidden rounded-bl-lg border-b border-l bg-white/85 text-sm">{[1, 2].map(_temp5)}</div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp5(i) {
  return <div key={i} className="border-t px-4 py-4 first:pt-5 last:pb-5"><div className="mb-3 flex items-center justify-between gap-2"><div className="bg-brand-grey/25 h-5 w-16 animate-pulse rounded" /><div className="bg-brand-grey/25 h-4 w-20 animate-pulse rounded" /></div><div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{[1, 2].map(_temp4)}</div><div className="mt-3 flex flex-col justify-between text-xs sm:flex-row sm:items-end"><div className="bg-brand-grey/25 h-3 w-28 animate-pulse rounded" /><div className="bg-brand-grey/25 h-3 w-24 animate-pulse rounded" /></div></div>;
}
function _temp4(j) {
  return <div key={j} className="flex flex-col space-y-1"><div className="bg-brand-grey/25 mb-2 h-4 w-24 animate-pulse rounded" /><div className="bg-brand-grey/25 h-6 w-32 animate-pulse rounded" /><div className="bg-brand-grey/25 mt-2 h-4 w-20 animate-pulse rounded" /></div>;
}